import { Box, Card, FormHelperText, Grid } from "@mui/material"
import { DebugBox, ReactHooksFormWrapper, SelectRequiredControl, TextAreaControl } from "@northpowerdev/np-react-utils"
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useFetchJson from "../../../data/useFetchJson";
import { RadioGroupRequiredControl } from "../../styled/RadioGroupControl";
import { DlspStatusList } from "./DlspStatusList";


const desktopQANextStatusOptions = [
    {key: 'desktop-qa',    value: 'Desktop QA',    label: 'Desktop QA'},
    {key: 'field',         value: 'Field',         label: 'Field'},
    {key: 'remedial',      value: 'Remedial',      label: 'Remedial'},
    {key: 'eliminated',    value: 'Eliminated',    label: 'Eliminated'},
]

const lowHighOptions = [
    {key: 'high', value: 'High', label: 'High (some risk at ICP, e.g. neutral still connected)'},
    {key: 'low',  value: 'Low',  label: 'Low'},
]

const yesNoOptions = [
    {key: 'yes', value: 'Yes', label: 'Yes'},
    {key: 'no',  value: 'No',  label: 'No'},
]

const initialFormState = {
    // Assessment Audit
    desktopQAComplete: null,                // 'Yes' | 'No'
    desktopQAComments: '',                  // 'free text'
    fieldPhotosRequired: '',                // free text
    landOwnerPermissionRequired: null,      // 'Yes'|'No'
    remedialPriority: null,                 // 'Low', 'High'
    // Decision Making
    nextStatus: '',                         // 'Desktop QA', 'Field', 'Remedial', 'Eliminated'
}

const defaultFieldPhotoText = `Wide area photo around service line and its connection to the network. 
Area around pole showing space for work access.
Bottom of pole showing pole number if possible.
Top of pole showing service line and fuses if possible.
A view from the pole along the service line towards the ICP location.
Anything unusual or hazardous in the area.`

export default function DlspDesktopQAForm({desktopQADetails, statusHistory, icpNumber, assignee, onClose}) {

    const [qaNextStatus, setQANextStatus] = useState()
    // const [desktopQACommentsHelperText, setDesktopQACommentsHelperText] = useState()
    // const [showFieldPhotosTextArea, setShowFieldPhotosTextArea] = useState()
    const [registryStatus, setRegistryStatus] = useState()
    const { handleSubmit, control, formState: { errors }, setValue, watch, /* reset, getValues, trigger */ } = useForm({defaultValues: initialFormState});
    const watchAllFields = watch()

    const {
        isLoading: desktopQASaving,
        load: saveDesktopQA,
        } = useFetchJson('/bff/decommissioned-lines-service/api/decommissioned-lines/submit-desktop-qa', {method: 'POST'}) 

    useEffect(() => {
        
        //
        // Triggers on start-up or assessment details change to pre-fill form fields
        // 
        if (desktopQADetails) {
            // QA Audit
            setValue('desktopQAComplete',            desktopQADetails.desktopQAComplete)
            setValue('desktopQAComments',            desktopQADetails.desktopQAComments)
            setValue('fieldPhotosRequired',          desktopQADetails.fieldPhotosRequired)
            setValue('landOwnerPermissionRequired',  desktopQADetails.landOwnerPermissionRequired || 'Yes')
            setValue('remedialPriority',             desktopQADetails.remedialPriority)
            
            // Decision Making
            setValue('nextStatus',                    desktopQADetails.nextStatus)

            // updateStatusHelperTextAndOptionalFields(desktopQADetails.nextStatus)
            setQANextStatus(desktopQADetails.nextStatus)
        }
    }, [desktopQADetails, setValue])

    useEffect(() => {
        if (statusHistory) {
            if (statusHistory.length) {
                const currentRegistryStatus = statusHistory[statusHistory.length - 1]
                setRegistryStatus(currentRegistryStatus.status)

            }
        }
    }, [setValue, statusHistory])

    const onSubmit = async (data) => {
        console.log('DlspDesktopQAForm onSubmit data', data)
        const desktopQAOptions = {
            params: {
                icp: icpNumber,
                nextStatus: data.nextStatus,
            },
            body: JSON.stringify(data)
        }

        const theAssessmentResponse = await saveDesktopQA(desktopQAOptions)
        if (theAssessmentResponse !== undefined) {
            //
            // Desktop QA form was saved successfully and status was updated. 
            //
            onClose() // tell parent component we're done
        }
    }

    const handleNewStatusChange = (newStatus) => {
        console.log('handleNewStatusChange event', newStatus)

        // updateStatusHelperTextAndOptionalFields(newStatus)
        setQANextStatus(newStatus)

        //
        // Set default fieldPhotosRequired text if this is a field inspection,
        // otherwise clear the fieldPhotosRequired box.
        //
        setValue('fieldPhotosRequired',         newStatus === 'Field' ? defaultFieldPhotoText : '')
        setValue('landOwnerPermissionRequired', newStatus === 'Field' ? 'Yes' : null)

        //
        // If the new status is not Remedial clear the remedialPriority value.
        //
        if (newStatus !== 'Remedial') {
            setValue('remedialPriority', null)
        }

    }

    // const updateStatusHelperTextAndOptionalFields = (newStatus) => {
    //     // let newHelperText
    //     // switch (newStatus) {
    //     //     case 'Field':
    //     //         newHelperText = 'These comments are instructions to field inspector'
    //     //         break
    //     //     case 'Remedial':
    //     //         newHelperText = 'These comments are instructions to Northpower Contracting'
    //     //         break
    //     //     default:
    //     //         newHelperText = ' '
    //     //         break
    //     // }
    //     // setDesktopQACommentsHelperText(newHelperText)

    //     // setShowFieldPhotosTextArea(newStatus === 'Field')
    // }

    let qaCommentsHelperText
    switch (qaNextStatus) {
        case 'Field':
            qaCommentsHelperText = 'These comments are instructions to field inspector'
            break
        case 'Remedial':
            qaCommentsHelperText = 'These comments are instructions to Northpower Contracting'
            break
        default:
            qaCommentsHelperText = ' '
            break
    }

    return (
    
        <Card sx={{my:2, p:2, bgcolor: 'transparent'}}>
        <ReactHooksFormWrapper
                onSubmit={handleSubmit(onSubmit)}
                control={control}
                errors={errors}
                submitLabel="Save Desktop QA"
                isSubmitting={desktopQASaving}
                submittingLabel="Saving desktop QA..."
        >

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>

                    <RadioGroupRequiredControl control={control} errors={errors} fieldName={'desktopQAComplete'}
                        fieldLabel="Desktop QA is Complete" controlSx={{my: 2}} options={yesNoOptions}
                    /> <br />
                    <Box sx={{mb: 2}}>
                        <TextAreaControl control={control} errors={errors} fieldName={'desktopQAComments'}
                            fieldLabel="Desktop QA Comments" controlSx={{width: '100%', mt: -2}}
                            minRows={5} maxRows={5}
                        />
                        <FormHelperText sx={{mt: -1}}>{qaCommentsHelperText}</FormHelperText>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>

                    <SelectRequiredControl
                        control={control}
                        errors={errors}
                        fieldName={'nextStatus'}
                        fieldLabel="Next task status"
                        options={desktopQANextStatusOptions}
                        controlSx={{minWidth: '80%'}}
                        onChange={(e) =>handleNewStatusChange(e.target.value)}
                    /> <br />

                    {
                        qaNextStatus === 'Field'
                        &&
                        <Box sx={{mb: 2}}>
                            <TextAreaControl control={control} errors={errors} fieldName={'fieldPhotosRequired'}
                                fieldLabel="Photos Required from Field Inspection" controlSx={{width: '100%'}}
                                minRows={4} maxRows={8}
                            />
                            <FormHelperText sx={{mt: -1}}>This list of photos goes to field inspector</FormHelperText>
                        </Box>
                    }

                    {
                        qaNextStatus === 'Field'
                        &&
                        <Box sx={{mb: 6}}>
                            <RadioGroupRequiredControl control={control} errors={errors} fieldName={'landOwnerPermissionRequired'}
                                fieldLabel="Get Land Owner Permission Before Field Inspection" controlSx={{my: 2}} options={yesNoOptions}
                            /> <br />
                        </Box>
                    }

                    {
                        qaNextStatus === 'Remedial'
                        &&
                        <Box sx={{mb: 2}}>
                            <RadioGroupRequiredControl control={control} errors={errors} fieldName={'remedialPriority'}
                                fieldLabel="Priority of Remedial Work" controlSx={{my: 2}} options={lowHighOptions}
                            /> <br />
                        </Box>
                    }


                </Grid>
                <Grid item xs={12} sm={6}>

                    <DlspStatusList registryStatus={registryStatus} />

                </Grid>
            </Grid>
                {/* <DebugBox title="desktopQADetails" stringify>{desktopQADetails}</DebugBox> */}
                <DebugBox title="qaNextStatus" stringify>{qaNextStatus}</DebugBox>
                <DebugBox title="errors" stringify>{errors}</DebugBox>
                <DebugBox title="watchAllFields" stringify>{watchAllFields}</DebugBox>
        </ReactHooksFormWrapper>
        </Card>
    )
}

